import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, Overline, SectionTitle, EmptyState } from "@/components/portal/Bits";
import { Users2, Trophy, Dumbbell, Link2, Instagram, ShieldAlert, Activity, Flame, Info } from "lucide-react";

function Stat({ label, value, hint, icon: Icon }) {
  return (
    <Panel className="p-5" data-testid={`stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="flex items-start justify-between">
        <Overline>{label}</Overline>
        {Icon && <Icon className="h-4 w-4 text-muted-foreground/60" />}
      </div>
      <div className="font-display text-4xl text-white mt-3">{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </Panel>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [o, setO] = useState(null);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/dashboard/overview");
      setO(data);
      if (data.is_admin) {
        try { const r = await api.get("/alerts"); setAlerts(r.data.alerts || []); } catch { /* ignore */ }
      }
    })();
  }, []);

  if (!o) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div>
      <SectionTitle sub={o.is_admin ? "Roster overview and data coverage across all clients." : "Your portal overview."}>
        Dashboard
      </SectionTitle>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Athletes" value={o.total_athletes} icon={Users2} hint="Confirmed public roster" />
        <Stat label="Football" value={o.football} icon={Trophy} />
        <Stat label="Basketball" value={o.basketball} icon={Dumbbell} />
        <Stat label="Snapshots" value={o.snapshots} icon={Activity} hint="Weekly data points stored" />
        <Stat label="ESPN mapped" value={`${o.espn_mapped}/${o.total_athletes}`} icon={Link2} hint="Athletes with an ESPN ID" />
        <Stat label="Instagram mapped" value={`${o.instagram_mapped}/${o.total_athletes}`} icon={Instagram} />
        <Stat label="PFF scores" value={o.pff_set} icon={ShieldAlert} hint="Empty pending license" />
        <Stat label="With history" value={o.athletes_with_history} icon={Activity} hint="≥2 weekly snapshots" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        <Panel className="p-6 lg:col-span-2">
          <div className="flex items-center gap-2 mb-3">
            <Flame className="h-4 w-4 text-[#E1FF00]" />
            <h3 className="font-display text-xl text-white">Heat alerts</h3>
          </div>
          {!o.is_admin ? (
            <EmptyState title="Alerts appear on your profile" hint="Open your profile to see engagement and performance alerts as data arrives." />
          ) : alerts.length === 0 ? (
            <EmptyState testId="dashboard-no-alerts" title="No alerts yet"
              hint="Heat alerts fire from real signals — engagement spikes, follower surges and on-field movement — once weekly snapshots exist." />
          ) : (
            <div className="space-y-2" data-testid="dashboard-alerts">
              {alerts.slice(0, 6).map((a, i) => (
                <Link key={i} to={`/athlete/${a.athlete_id}`} className="flex items-center justify-between rounded-md border border-border px-4 py-3 hover:bg-secondary/60 transition-colors">
                  <div>
                    <div className="text-sm font-semibold text-white">{a.athlete_name} · {a.title}</div>
                    <div className="text-xs text-muted-foreground">{a.detail}</div>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider text-[#E1FF00]">{a.severity}</span>
                </Link>
              ))}
            </div>
          )}
        </Panel>

        <Panel className="p-6">
          <div className="flex items-center gap-2 mb-3">
            <Info className="h-4 w-4 text-muted-foreground" />
            <h3 className="font-display text-xl text-white">Data integrity</h3>
          </div>
          <ul className="text-sm text-muted-foreground space-y-3 leading-relaxed">
            <li>Only names, sports and schools are confirmed from the public roster.</li>
            <li>Every other field reads <span className="text-white">Not verified</span> / <span className="text-white">No data yet</span> until sourced.</li>
            <li>ESPN stats pull live from public endpoints with source + timestamps.</li>
            <li>PFF stays empty pending a commercial license; Instagram needs an authorized API.</li>
          </ul>
        </Panel>
      </div>
    </div>
  );
}
